import { useQuery } from "@tanstack/react-query";

export interface BrowseMenuItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  priceMin: number;
  priceMax: number;
  priceDisplay: string;
  imageUrl?: string;
  menuPeriods: string[];
  originalItems: BrowseMenuOriginalItem[];
}

export interface BrowseMenuOriginalItem {
  id: string;
  price: number;
  menuPeriod: string;
  category: string;
}

export interface BrowseMenuCategory {
  name: string;
  description?: string;
  items: BrowseMenuItem[];
}

export interface BrowseMenuResponse {
  restaurantId: string;
  restaurantName: string;
  totalItems: number;
  totalCategories: number;
  categories: BrowseMenuCategory[];
  rawItemCount: number;
  deduplicatedItemCount: number;
}

interface RawMenuItem {
  id: string;
  name: string;
  description: string | null;
  price: string;
  imageUrl: string | null;
  isAvailable: boolean;
  allergens: string[] | null;
  modifiers?: unknown;
}

interface RawMenuCategory {
  name: string;
  description?: string;
  items: RawMenuItem[];
}

interface RawMenuData {
  restaurantId: string;
  restaurantName: string;
  totalItems: number;
  totalCategories: number;
  categories: RawMenuCategory[];
  menuInfo?: {
    name: string;
    description?: string;
  };
}

function deduplicateMenuItems(rawData: RawMenuData): BrowseMenuResponse {
  const itemsByName = new Map<string, {
    items: { id: string; price: number; category: string; menuPeriod: string }[];
    description?: string;
    imageUrl?: string;
  }>();

  let rawItemCount = 0;

  rawData.categories.forEach(category => {
    const menuPeriod = rawData.menuInfo?.name || category.name;
    
    category.items.forEach(item => {
      rawItemCount++;
      const normalizedName = item.name.trim().toLowerCase();
      const price = parseFloat(item.price) || 0;
      
      if (!itemsByName.has(normalizedName)) {
        itemsByName.set(normalizedName, {
          items: [],
          description: item.description || undefined,
          imageUrl: item.imageUrl || undefined,
        });
      }
      
      const existing = itemsByName.get(normalizedName)!;
      existing.items.push({
        id: item.id,
        price,
        category: category.name,
        menuPeriod,
      });
      
      if (!existing.description && item.description) {
        existing.description = item.description;
      }
      if (!existing.imageUrl && item.imageUrl) {
        existing.imageUrl = item.imageUrl;
      }
    });
  });

  const categoryMap = new Map<string, BrowseMenuItem[]>();
  const categoryDescriptions = new Map<string, string>();
  
  // Collect category descriptions from raw data
  rawData.categories.forEach(category => {
    if (category.description && !categoryDescriptions.has(category.name)) {
      categoryDescriptions.set(category.name, category.description);
    }
  });

  itemsByName.forEach((data, normalizedName) => {
    const prices = data.items.map(i => i.price).sort((a, b) => a - b);
    const priceMin = prices[0];
    const priceMax = prices[prices.length - 1];
    
    let priceDisplay: string;
    if (priceMin === priceMax) {
      priceDisplay = `$${priceMin.toFixed(2)}`;
    } else {
      priceDisplay = `$${priceMin.toFixed(2)} - $${priceMax.toFixed(2)}`;
    }

    const primaryCategory = data.items[0].category;
    const menuPeriods = Array.from(new Set(data.items.map(i => i.menuPeriod)));

    const originalName = data.items[0].id.split('_')[0] || normalizedName;
    const displayName = normalizedName.split(' ').map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    const browseItem: BrowseMenuItem = {
      id: data.items[0].id,
      name: displayName,
      description: data.description,
      category: primaryCategory,
      priceMin,
      priceMax,
      priceDisplay,
      imageUrl: data.imageUrl,
      menuPeriods,
      originalItems: data.items.map(i => ({
        id: i.id,
        price: i.price,
        menuPeriod: i.menuPeriod,
        category: i.category,
      })),
    };

    if (!categoryMap.has(primaryCategory)) {
      categoryMap.set(primaryCategory, []);
    }
    categoryMap.get(primaryCategory)!.push(browseItem);
  });

  const categories: BrowseMenuCategory[] = Array.from(categoryMap.entries()).map(
    ([name, items]) => ({ 
      name, 
      description: categoryDescriptions.get(name),
      items 
    })
  );

  return {
    restaurantId: rawData.restaurantId,
    restaurantName: rawData.restaurantName,
    totalItems: itemsByName.size,
    totalCategories: categories.length,
    categories,
    rawItemCount,
    deduplicatedItemCount: itemsByName.size,
  };
}

export function useBrowseMenu(restaurantId: string | undefined, enabled: boolean = true) {
  const query = useQuery<BrowseMenuResponse>({
    queryKey: ['/api/restaurants', restaurantId, 'menu', 'browse'],
    queryFn: async () => {
      if (!restaurantId) {
        throw new Error("Restaurant ID required");
      }

      const response = await fetch(`/api/restaurants/${restaurantId}/menu`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to fetch menu");
      }
      
      const rawData: RawMenuData = await response.json();
      return deduplicateMenuItems(rawData);
    },
    enabled: !!restaurantId && enabled,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  
  return {
    ...query,
    error: query.error,
    isMenuEmpty: query.data?.totalItems === 0 && !query.isLoading && !query.error,
  };
}
